import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';

interface UserAvatarProps {
  userId: string;
  fullName: string;
  photoUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZE_CLASS = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-9 w-9 text-sm',
  lg: 'h-20 w-20 text-2xl',
};

/**
 * Authenticated avatar: fetches photo with JWT (img tags cannot send Authorization).
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  userId,
  fullName,
  photoUrl,
  size = 'md',
  className = '',
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const load = async () => {
      if (!photoUrl) {
        setBlobUrl(null);
        return;
      }
      try {
        const res = await api.get(photoUrl, { responseType: 'blob' });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      } catch {
        if (!cancelled) setBlobUrl(null);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photoUrl, userId]);

  const sizeClass = SIZE_CLASS[size];
  const initial = fullName?.charAt(0)?.toUpperCase() || 'U';

  if (blobUrl) {
    return (
      <img
        src={blobUrl}
        alt={fullName}
        className={`${sizeClass} rounded-full object-cover flex-shrink-0 ${className}`}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold flex-shrink-0 ${className}`}
    >
      {initial}
    </div>
  );
};
